/**
 * JUB-154: the wizard's screens, derived from the answers so far.
 *
 * Pure functions only — the page keeps the current step in the URL, so the
 * browser Back button works and every screen can be reloaded from the server.
 * Order (§21-23 of the ticket):
 *   type → people → date → [combined, wedding+baptism only] → structure →
 *   per enabled moment: time, location → recap → rsvp-deadline →
 *   rsvp-questions → generating → preview
 */
import { MOMENT_SLOTS, type MomentSlot, type OnboardingAnswers, type OnboardingPatch } from "./types";

export type StepId =
  | "type"
  | "people"
  | "date"
  | "combined"
  | "structure"
  | `moment-${MomentSlot}-time`
  | `moment-${MomentSlot}-location`
  | "recap"
  | "rsvp-deadline"
  | "rsvp-questions"
  | "generating"
  | "preview";

/** The four stages the customer sees (§2): Despre voi → Program → Confirmări → Gata. */
export type StepGroup = "about" | "program" | "rsvp" | "done";
export const STEP_GROUPS: StepGroup[] = ["about", "program", "rsvp", "done"];

type Answers = Pick<OnboardingAnswers, "type" | "moments">;

export function getSteps(answers: Answers): StepId[] {
  const steps: StepId[] = ["type", "people", "date"];
  if (answers.type === "wedding_baptism") steps.push("combined");
  steps.push("structure");
  for (const slot of MOMENT_SLOTS) {
    if (answers.moments[slot]?.enabled) {
      steps.push(`moment-${slot}-time`, `moment-${slot}-location`);
    }
  }
  steps.push("recap", "rsvp-deadline", "rsvp-questions", "generating", "preview");
  return steps;
}

export function isStepId(value: string | null | undefined): value is StepId {
  if (!value) return false;
  if (/^moment-(civil|religious|baptism|party)-(time|location)$/.test(value)) return true;
  return ["type", "people", "date", "combined", "structure", "recap", "rsvp-deadline", "rsvp-questions", "generating", "preview"].includes(value);
}

export function momentOf(step: StepId): { slot: MomentSlot; part: "time" | "location" } | null {
  const match = /^moment-(civil|religious|baptism|party)-(time|location)$/.exec(step);
  return match ? { slot: match[1] as MomentSlot, part: match[2] as "time" | "location" } : null;
}

/**
 * A step that is no longer part of the flow (e.g. the moment was switched
 * off) resolves to the structure screen, where the change was made.
 */
export function resolveStep(step: string | null | undefined, answers: Answers): StepId {
  if (!isStepId(step)) return "people";
  return getSteps(answers).includes(step) ? step : "structure";
}

export function nextStep(step: StepId, answers: Answers): StepId | null {
  const steps = getSteps(answers);
  const index = steps.indexOf(resolveStep(step, answers));
  return index >= 0 && index < steps.length - 1 ? steps[index + 1] : null;
}

export function prevStep(step: StepId, answers: Answers): StepId | null {
  const steps = getSteps(answers);
  const index = steps.indexOf(resolveStep(step, answers));
  return index > 0 ? steps[index - 1] : null;
}

export function groupOf(step: StepId | string | null | undefined): StepGroup {
  if (!step || step === "type" || step === "people" || step === "date") return "about";
  if (step === "rsvp-deadline" || step === "rsvp-questions") return "rsvp";
  if (step === "generating" || step === "preview" || step === "done") return "done";
  return "program";
}

/**
 * Percentage shown on the dashboard resume card. The preview tops out at 95:
 * 100 % is reserved for the explicit exit that completes the wizard.
 */
export function progressOf(step: StepId, answers: Answers): number {
  const steps = getSteps(answers);
  const index = Math.max(0, steps.indexOf(resolveStep(step, answers)));
  return Math.min(95, Math.round((index / (steps.length - 1)) * 100));
}

/** Within-group position, e.g. "Program 2/6" on the progress header. */
export function groupProgress(step: StepId, answers: Answers): { done: number; total: number } {
  const group = groupOf(step);
  const inGroup = getSteps(answers).filter((s) => groupOf(s) === group);
  return { done: Math.max(0, inGroup.indexOf(step)), total: inGroup.length };
}

/** §20: type and people are never skippable; date, times, places and the RSVP deadline are. */
export function isSkippable(step: StepId): boolean {
  return step === "date" || step === "rsvp-deadline" || momentOf(step) !== null;
}

/** The API step a screen saves into, with the slot/part for moment screens. */
export function apiStepFor(step: StepId): Pick<OnboardingPatch, "step" | "data"> {
  const moment = momentOf(step);
  if (moment) return { step: "moment", data: { slot: moment.slot, part: moment.part } };
  switch (step) {
    case "combined":
      return { step: "structure" };
    case "rsvp-deadline":
      return { step: "rsvp_deadline" };
    case "rsvp-questions":
      return { step: "rsvp_questions" };
    default:
      return { step };
  }
}
